/**
 * W5 canonical cast and home-capital placement.
 *
 * Definitions are authored once; runtime identities are scoped to the owner of
 * the generated home system. Placement is rebuilt from semantic parcel uses,
 * never a parcel array index persisted across CityGraph versions.
 */
import { PlanetSampler, homeOwner, systemDefFor } from "../universe.js";
import type { NpcDef, NpcJobKind, NpcRole } from "../npcs.js";
import type { CharacterStyle } from "../rpg.js";
import { generateNpcPersonality, type NpcPersonality } from "../social/personality.js";
import {
  TIER_TOWN,
  type TierAssignment,
  tierRegistryFor,
} from "../worldgen/tierRegistry.js";
import { unpackCell, type CellId } from "../worldgen/cells.js";
import {
  USE_ADMIN,
  USE_DEPOT,
  USE_GARRISON,
  USE_HALL,
  USE_HEADMAN,
  USE_LANDING_PAD,
  USE_MARKET_HALL,
  USE_PALACE,
  USE_WATCHTOWER,
  type CityGraph,
} from "../worldgen/citygraph.js";
import { materializeRegion } from "../worldgen/growth.js";
import { collapseRegion, type RegionChronicle, type SettlementChronicle } from "../worldgen/collapse.js";
import { regionSettlements, type RegionSettlement } from "../worldgen/settlements.js";
import { civicApproach } from '../worldgen/civics.js';

/** NPC ids embed the FULL home-system id (multi-character: `home:<uid>.<cid>`),
 *  so a canon NPC belongs to one character's capital, not to the account. */
const CANON_ID_RE = /^(home:[A-Za-z0-9_-]{1,64}(?:\.c\d{1,3})?):canon:([a-z0-9]+(?:-[a-z0-9]+)*)$/;

export type CanonicalNpcSlug = "mara-venn" | "sable-rook";
export type CanonicalAnchorKind = "capital-civic" | "capital-field";

export interface CanonicalNpcDefinition {
  slug: CanonicalNpcSlug;
  name: string;
  role: NpcRole;
  traits: readonly [string, string];
  personality: NpcPersonality;
  style: Readonly<CharacterStyle>;
  anchor: CanonicalAnchorKind;
  job: NpcJobKind;
  /** Story events this definition is allowed to remember in StoryStateV1. */
  events: readonly string[];
}

// Post-clothing-purge (2026-07-31): procedural variants are gone — canon cast
// dresses in drifter pieces, differentiated by coverage + palette. Mara Venn
// is the wayfarer (female base body).
const KEEPER_STYLE: Readonly<CharacterStyle> = Object.freeze({
  body: "female", head: "none", torso: "drifter", arms: "none",
  legs: "drifter", feet: "drifter",
  suit: 0x334654, accent: 0xd6a84b,
});

const OPERATIVE_STYLE: Readonly<CharacterStyle> = Object.freeze({
  body: "male", head: "drifter", torso: "drifter", arms: "drifter",
  legs: "drifter", feet: "drifter",
  suit: 0x273139, accent: 0x42c2b3,
});

/** Authored identity only. Mutable player-specific knowledge belongs in StoryState. */
export const CANONICAL_NPCS: readonly CanonicalNpcDefinition[] = Object.freeze([
  Object.freeze({
    slug: "mara-venn",
    name: "Mara Venn",
    role: "keeper",
    traits: ["measured", "protective"] as const,
    personality: generateNpcPersonality({ identity: ["canon-definition", "mara-venn"], traits: ["measured", "protective"],
      overrides: { ref: "canon:mara-venn:personality:1", dispositions: {
        sociability: 48, assertiveness: 72, compassion: 82, politeness: 76, diligence: 78,
        curiosity: 56, candor: 75, stressSensitivity: 30,
      }, values: ["loyalty", "security"], interest: "gardens", habit: "considers_before_answering",
      expression: { length: "brief", manner: "gentle", gesture: "restrained" } } }),
    style: KEEPER_STYLE,
    anchor: "capital-civic",
    job: "hall",
    events: ["introduced", "briefed", "debriefed"],
  }),
  Object.freeze({
    slug: "sable-rook",
    name: "Sable Rook",
    role: "warden",
    traits: ["incisive", "restless"] as const,
    personality: generateNpcPersonality({ identity: ["canon-definition", "sable-rook"], traits: ["incisive", "restless"],
      overrides: { ref: "canon:sable-rook:personality:1", dispositions: {
        sociability: 38, assertiveness: 85, compassion: 54, politeness: 35, diligence: 70,
        curiosity: 78, candor: 80, stressSensitivity: 38,
      }, values: ["autonomy", "mastery"], interest: "tools", habit: "brief_glance_away",
      expression: { length: "brief", manner: "dry", gesture: "restrained" } } }),
    style: OPERATIVE_STYLE,
    anchor: "capital-field",
    job: "patrol",
    events: ["met", "deployed", "returned"],
  }),
]);

const DEFINITIONS = new Map(CANONICAL_NPCS.map((definition) => [definition.slug, definition]));

export function canonicalNpcDefinition(slug: string): CanonicalNpcDefinition | null {
  return DEFINITIONS.get(slug as CanonicalNpcSlug) ?? null;
}

export function canonicalNpcId(homeId: string, slug: CanonicalNpcSlug): string | null {
  return homeOwner(homeId) !== null && DEFINITIONS.has(slug) ? `${homeId}:canon:${slug}` : null;
}

export function parseCanonicalNpcId(
  id: unknown,
): { home: string; uid: string; slug: CanonicalNpcSlug } | null {
  if (typeof id !== "string") return null;
  const match = CANON_ID_RE.exec(id);
  if (!match || !DEFINITIONS.has(match[2] as CanonicalNpcSlug)) return null;
  return { home: match[1], uid: homeOwner(match[1])!, slug: match[2] as CanonicalNpcSlug };
}

/** The explicit home-capital rule: tier desc, suitability desc, address asc. */
export function homeCapitalAssignment(sampler: PlanetSampler): TierAssignment | null {
  if (sampler.def.home !== true) return null;
  let best: TierAssignment | null = null;
  for (const assignments of tierRegistryFor(sampler).byCell.values()) {
    for (const candidate of assignments) {
      if (candidate.tier < TIER_TOWN) continue;
      if (best === null
        || candidate.tier > best.tier
        || (candidate.tier === best.tier && candidate.suit > best.suit)
        || (candidate.tier === best.tier && candidate.suit === best.suit && candidate.cellKey < best.cellKey)
        || (candidate.tier === best.tier && candidate.suit === best.suit
          && candidate.cellKey === best.cellKey && candidate.k < best.k)) best = candidate;
    }
  }
  return best === null ? null : { ...best };
}

export interface CanonicalNpcPlacement {
  systemId: string;
  ownerUid: string;
  planetIndex: 0;
  regionId: CellId;
  settlement: RegionSettlement;
  npc: NpcDef;
  anchor: {
    kind: "parcel" | "board" | "center";
    parcel: number | null;
    use: number | null;
  };
}

/** Instantiate definitions into an already-derived capital. This is the fast
 * client path: region residency has already materialized graph + chronicle. */
export function canonicalCastAtCapital(
  homeId: string,
  graph: CityGraph,
  chronicle: RegionChronicle,
  settlement: RegionSettlement,
): CanonicalNpcPlacement[] {
  const ownerUid = homeOwner(homeId);
  if (ownerUid === null) return [];
  return CANONICAL_NPCS.map((definition) => {
    const id = canonicalNpcId(homeId, definition.slug)!;
    const anchor = semanticAnchor(graph, chronicle, settlement, definition.anchor);
    const style: CharacterStyle = { ...definition.style };
    const npc: NpcDef = {
      id,
      name: definition.name,
      role: definition.role,
      traits: [definition.traits[0], definition.traits[1]],
      personality: definition.personality,
      style,
      u: anchor.u,
      v: anchor.v,
      roam: definition.job === "patrol" ? 8 : 2,
      home: -1,
      job: definition.job,
      jobData: [anchor.u, anchor.v, anchor.u, anchor.v],
      source: "canon",
    };
    return {
      systemId: homeId,
      ownerUid,
      planetIndex: 0 as const,
      regionId: { ...graph.regionId },
      settlement,
      npc,
      anchor: { kind: anchor.kind, parcel: anchor.parcel, use: anchor.use },
    };
  });
}

function settlementParcelRefs(settlement: SettlementChronicle): number[] {
  const refs = new Set<number>();
  for (const entry of settlement.entries) {
    if (entry.kind !== "district-founded") continue;
    for (const ref of entry.refs) refs.add(ref);
  }
  return [...refs].sort((a, b) => a - b);
}

function parcelCentroid(graph: CityGraph, ref: number): [number, number] {
  const poly = graph.parcels[ref]?.poly;
  if (!poly || poly.length === 0) return [0, 0];
  let u = 0, v = 0;
  for (let i = 0; i < poly.length; i += 2) { u += poly[i]; v += poly[i + 1]; }
  const count = poly.length / 2;
  return [u / count, v / count];
}

const ANCHOR_USES: Record<CanonicalAnchorKind, readonly number[]> = {
  "capital-civic": [USE_ADMIN, USE_HALL, USE_HEADMAN, USE_PALACE, USE_MARKET_HALL],
  "capital-field": [USE_GARRISON, USE_WATCHTOWER, USE_LANDING_PAD, USE_DEPOT],
};

function semanticAnchor(
  graph: CityGraph,
  chronicle: RegionChronicle,
  settlement: RegionSettlement,
  kind: CanonicalAnchorKind,
): { u: number; v: number; kind: "parcel" | "board" | "center"; parcel: number | null; use: number | null } {
  const civic = graph.civics?.find(site => site.seedK === settlement.seedK);
  if (kind === 'capital-civic' && civic) {
    return { ...civicApproach(civic, 'admin'), kind: 'parcel', parcel: civic.admin.parcel, use: USE_ADMIN };
  }
  const history = chronicle.settlements.find((item) => item.seedK === settlement.seedK);
  if (history) {
    const refs = settlementParcelRefs(history);
    const priorities = ANCHOR_USES[kind];
    let winner: { rank: number; dist: number; ref: number; u: number; v: number; use: number } | null = null;
    for (const ref of refs) {
      const use = graph.parcels[ref]?.use;
      const rank = priorities.indexOf(use);
      if (rank < 0) continue;
      const [u, v] = parcelCentroid(graph, ref);
      const du = u - settlement.u, dv = v - settlement.v;
      const candidate = { rank, dist: du * du + dv * dv, ref, u, v, use };
      if (!winner || candidate.rank < winner.rank
        || (candidate.rank === winner.rank && candidate.dist < winner.dist)
        || (candidate.rank === winner.rank && candidate.dist === winner.dist && candidate.ref < winner.ref)) winner = candidate;
    }
    if (winner) return { u: winner.u, v: winner.v, kind: "parcel", parcel: winner.ref, use: winner.use };
  }
  if (settlement.board) return { ...settlement.board, kind: "board", parcel: null, use: null };
  return { ...(settlement.arrival ?? settlement), kind: "center", parcel: null, use: null };
}

/** Resolve the complete home cast with one registry/materialization pass. */
export function resolveHomeCanonicalCast(homeId: string): CanonicalNpcPlacement[] {
  if (homeOwner(homeId) === null) return [];
  const system = systemDefFor(homeId);
  if (!system) return [];
  const sampler = new PlanetSampler(system.planets[0]);
  const capital = homeCapitalAssignment(sampler);
  if (!capital) return [];
  const regionId = unpackCell(capital.cellKey);
  const graph = materializeRegion(sampler, regionId);
  const chronicle = collapseRegion(sampler, graph);
  const settlement = regionSettlements(sampler, graph, chronicle)
    .find((item) => item.seedK === capital.k);
  if (!settlement) return [];
  return canonicalCastAtCapital(homeId, graph, chronicle, settlement);
}

/** Pure shared resolver used by both client and server validation paths. */
export function resolveHomeCanonicalNpc(homeId: string, slug: CanonicalNpcSlug): CanonicalNpcPlacement | null {
  if (homeOwner(homeId) === null || !DEFINITIONS.has(slug)) return null;
  return resolveHomeCanonicalCast(homeId).find((placement) => placement.npc.id === canonicalNpcId(homeId, slug)) ?? null;
}
